import React, { useState, useEffect } from 'react';
import { UserCog, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AuthService, ImpersonationUser } from '../../services/authService';

/**
 * Banner + brukervelger for felles utviklerbrukere (JWT-claim UserType=Developer).
 *
 * - <ImpersonationBanner/> (global, App.tsx): under impersonering (claim ImpersonatedBy)
 *   et tydelig banner øverst med hvem man ser som, og «Avslutt» som gjenoppretter
 *   dev-tokenet uten ny innlogging.
 * - <DeveloperUserBadge/> (inline i statuslinjen nederst på dashbordene): diskret merke
 *   «Utviklerbruker» + «Se som bruker…» som åpner en velger over alle brukere i aktiv tenant.
 *
 * Begge rendres kun når claims tilsier det — usynlige for alle vanlige brukere.
 * Tekst er bevisst norsk uten i18n (utviklerrettet, jf. MaintenanceBanner).
 */
export const ImpersonationBanner: React.FC = () => {
    const { user, stopImpersonation } = useAuth();

    if (!user?.impersonatedBy) return null;

    // sticky (ikke fixed) — banneret står hele økten og skal dytte innholdet ned,
    // ikke legge seg over toppmenyen.
    return (
        <div className="sticky top-0 z-[54] px-4 py-2 flex items-center justify-center gap-3 shadow-md bg-indigo-600 text-white">
            <UserCog size={18} className="flex-shrink-0" />
            <span className="text-sm font-medium">
                Du ser som <span className="font-bold">{user.operatorName || user.operatorId}</span>
                {' '}({user.operatorId}) — utvikler: {user.impersonatedBy}
            </span>
            <button
                onClick={stopImpersonation}
                className="ml-2 px-3 py-1 text-xs font-semibold rounded bg-white/20 hover:bg-white/30 transition-colors"
            >
                Avslutt
            </button>
        </div>
    );
};

interface DeveloperUserBadgeProps {
    className?: string;
}

/**
 * Inline-merke for utviklerbrukeren selv, plassert i statuslinjen nederst (ved «Hjelp»).
 * Rendres ikke under impersonering eller for vanlige brukere.
 */
export const DeveloperUserBadge: React.FC<DeveloperUserBadgeProps> = ({ className }) => {
    const { user, impersonate } = useAuth();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [users, setUsers] = useState<ImpersonationUser[]>([]);
    const [filter, setFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!pickerOpen) return;
        setLoading(true);
        setError(null);
        AuthService.getImpersonationUsers()
            .then(setUsers)
            .catch((e: Error) => setError(e.message))
            .finally(() => setLoading(false));
    }, [pickerOpen]);

    if (!user || user.impersonatedBy || !user.isDeveloper) return null;

    const filteredUsers = users.filter(u => {
        const q = filter.toLowerCase();
        return !q || u.userID.toLowerCase().includes(q) || (u.profileName || '').toLowerCase().includes(q);
    });

    const handlePick = async (userId: string) => {
        setError(null);
        try {
            await impersonate(userId);
        } catch (e) {
            setError((e as Error).message);
        }
    };

    return (
        <>
            <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-indigo-600 text-white ${className || ''}`}>
                <UserCog size={14} className="flex-shrink-0" />
                <span className="text-xs font-semibold whitespace-nowrap">Utviklerbruker</span>
                <button
                    onClick={() => setPickerOpen(true)}
                    className="px-2 py-0.5 text-xs font-medium rounded-full bg-white/20 hover:bg-white/30 transition-colors whitespace-nowrap"
                >
                    Se som bruker…
                </button>
            </span>

            {pickerOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => setPickerOpen(false)}>
                    <div
                        className="w-full max-w-md mx-4 rounded-lg shadow-xl bg-[var(--color-surface,#fff)] text-[var(--color-text-primary,#111)] flex flex-col max-h-[70vh]"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border,#e5e7eb)]">
                            <h3 className="text-sm font-semibold flex items-center gap-2">
                                <UserCog size={16} /> Se som bruker
                            </h3>
                            <button onClick={() => setPickerOpen(false)} className="p-1 rounded hover:bg-black/10">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="px-4 py-2 border-b border-[var(--color-border,#e5e7eb)]">
                            <input
                                type="text"
                                value={filter}
                                onChange={e => setFilter(e.target.value)}
                                placeholder="Søk på bruker-ID eller navn…"
                                autoFocus
                                className="w-full px-3 py-1.5 text-sm rounded border border-[var(--color-border,#d1d5db)] bg-transparent focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>

                        <div className="overflow-y-auto flex-1">
                            {loading && <div className="px-4 py-6 text-sm text-center opacity-70">Henter brukere…</div>}
                            {error && <div className="px-4 py-3 text-sm text-red-600">{error}</div>}
                            {!loading && !error && filteredUsers.length === 0 && (
                                <div className="px-4 py-6 text-sm text-center opacity-70">Ingen brukere funnet</div>
                            )}
                            {filteredUsers.map(u => (
                                <button
                                    key={`${u.companyNo}:${u.userID}`}
                                    onClick={() => handlePick(u.userID)}
                                    className="w-full text-left px-4 py-2.5 text-sm border-b border-[var(--color-border,#f3f4f6)] hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                                >
                                    <span className="font-medium">{u.userID}</span>
                                    {u.profileName && <span className="ml-2 opacity-70">{u.profileName}</span>}
                                    <span className="float-right text-xs opacity-60">
                                        {u.companyNo && `Selskap ${u.companyNo}`}
                                        {u.profileGroupID != null && ` · Gruppe ${u.profileGroupID}`}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
